// src/components/home/PartnersCarousel.jsx
import { motion } from "framer-motion";
import { useRef } from "react";
import { useEffect, useState, useCallback } from "react";
import { Handshake } from "lucide-react";

// Replace with your real partner logos
const partnerLogos = [
    { name: "Partner A", logo: `${process.env.PUBLIC_URL}/assets/partners/partner1.png` },
    { name: "Partner B", logo: `${process.env.PUBLIC_URL}/assets/partners/partner2.png` },
    { name: "Partner C", logo: `${process.env.PUBLIC_URL}/assets/partners/partner3.jpg` },
    { name: "Partner D", logo: `${process.env.PUBLIC_URL}/assets/partners/partner4.png` },
    { name: "Partner E", logo: `${process.env.PUBLIC_URL}/assets/partners/partner5.png` },
  ];

const PartnersCarousel = () => {
  const scrollRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  // Improved auto-scroll functionality
  const autoScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container || !isAutoScrolling) return;

    const scrollStep = 1; // Smaller step for smoother movement
    const maxScroll = container.scrollWidth - container.clientWidth;

    if (container.scrollLeft >= maxScroll) {
      // Reset to beginning instantly for seamless loop
      container.scrollLeft = 0;
      setIsAtEnd(false);
    } else {
      container.scrollLeft += scrollStep;

      // Check if we're approaching the end
      if (container.scrollLeft >= maxScroll - 10) {
        setIsAtEnd(true);
      }
    }
  }, [isAutoScrolling]);

  useEffect(() => {
    let scrollInterval;

    if (isAutoScrolling) {
      scrollInterval = setInterval(autoScroll, 30); // Faster interval for smoother movement
    }

    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [isAutoScrolling, autoScroll]);

  // Pause auto-scroll on hover with debounce
  const handleMouseEnter = useCallback(() => {
    setIsAutoScrolling(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Small delay to prevent immediate restart
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 100);
  }, []);

  // Handle touch events for mobile
  const handleTouchStart = useCallback(() => {
    setIsAutoScrolling(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setTimeout(() => {
      setIsAutoScrolling(true);
    }, 500);
  }, []);

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-secondary/5 to-primary/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      {/* Custom styles for glassmorphism and scrollbar hiding */}
      <style>{`
        .glass-card {
          background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(186,230,253,0.3) 100%);
          backdrop-filter: blur(10px) saturate(180%);
          border-radius: 1.5rem;
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.08);
          border: 1px solid rgba(56,189,248,0.15);
          transition: all 0.4s ease;
        }

        .glass-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px 0 rgba(31, 38, 135, 0.15);
          border-color: rgba(56,189,248,0.3);
        }

        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
          scroll-behavior: auto;
        }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }

        .logo-container {
          background: white;
          border-radius: 1rem;
          padding: 1.5rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100px;
          will-change: transform;
        }

        .logo-container:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }

        .carousel-container {
          position: relative;
          overflow: hidden;
        }

        .carousel-track {
          display: flex;
          gap: 2rem;
          transition: none;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
        <motion.div
          className="flex items-center justify-center gap-4 mb-6"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white shadow-lg">
            <Handshake className="w-6 h-6" />
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
            Our Partners
          </h2>
        </motion.div>

        <motion.p
          className="text-lg text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed font-body"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Collaborating with leading organizations to create lasting social impact and drive positive change
        </motion.p>

        <div 
          className="carousel-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={scrollRef}
            className="carousel-track overflow-x-auto no-scrollbar py-4 px-4"
          >
            {/* Triple the logos for seamless infinite scroll */}
            {[...partnerLogos, ...partnerLogos, ...partnerLogos].map((partner, index) => (
              <motion.div
                key={index}
                className="logo-container min-w-[200px] md:min-w-[240px] flex-shrink-0"
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  delay: (index % partnerLogos.length) * 0.1, 
                  duration: 0.6, 
                  type: "spring", 
                  stiffness: 100,
                }}
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="w-full h-16 object-contain"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnersCarousel;
